import styled from "styled-components";
import { useParams, useLocation } from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {useMasking} from "../../common/hook";
import moment from "moment";
import DatepickerRange from "../molecules/DatepickerRange";
import { useMutation, useQuery } from "@tanstack/react-query";
import Loading from "./Loading";
import {useState} from "react";
import SelectBox from "../molecules/SelectBox";
import DataTable from "../organisms/DataTable";
import axios from "axios";
import { Button, Col, Form, Row } from "react-bootstrap";
import TableFilterBox from "../organisms/TableFilterBox";
import TableHeader from "../organisms/TableHeader";
import TableFooter from "../organisms/TableFooter";
import TablePagination from "../molecules/TablePagination";


const TableWrap = styled.div`
    flex: 1 1;
    display: flex;
    flex-direction: column;

    .data-table {
        flex-grow: 1;
    }

    .revisit-badge {
        background: #007bff;
        color: white;
        padding: 4px 8px;
        border-radius: 12px;
        font-weight: bold;
        animation: pulse 1.5s infinite;
    }

    .cancel-badge {
        background: #dc3545;
        color: white;
        padding: 4px 8px;
        border-radius: 12px;
        font-weight: bold;
    }

    .report-ready-badge {
        background: #90ee90; /* 예쁜연두색 */
        color: white;
        padding: 4px 8px;
        border-radius: 12px;
        font-weight: bold;
    }

    @keyframes pulse {
        0% {
            transform: scale(1);
        }
        50% {
            transform: scale(1.05);
        }
        100% {
            transform: scale(1);
        }
    }
`;

const Worklist = () => {
  const location = useLocation();
  //default Date는 오늘
  const defaultDate = moment().format("YYYY-MM-DD");

  const dispatch = useDispatch();
  const {masking} = useMasking();
  const perPageOptions = useSelector(state => state.perPageOptions);
  const defaultFilter = {
    startDate: defaultDate,
    endDate: defaultDate,
    branchId: '0',
    page: 1,
    perPage: perPageOptions[1].value,
    orderField: "register_time",
    order: "desc",
    prescriptionStatus: "ALL"
  }


  // (S) state
  const [filter, setFilter] = useState(defaultFilter);
  const [loading, setLoading] = useState(false);
  const [worklistData, setWorklistData] = useState({
    orderField: ["branch_name", "register_time",  "name", "birth", "gender", "is_revisit", "status", "prescription_status"],
    head: [
      "지점", "등록일시",  "이름", "생년월일", "성별", "재방문 여부", "현재상태", "처방상태"
    ],
    body: [],
  });
  




  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['worklist', filter],
    queryFn: async () => {
      try {
        const response = await axios.post('/worklist/list', {
          branchId: filter.branchId,
          startDate: filter.startDate,
          endDate: filter.endDate,
          page: filter.page,
          perPage: filter.perPage,
          orderField: filter.orderField,
          order: filter.order,
          prescriptionStatus: filter.prescriptionStatus
        });
        const formattedData = response.data.data.list.map(item => [
          item.branchName || '-',
          item.registerTime || '-',
          masking(item.name || ''),
          masking(item.birth || ''),
          item.gender === 1 ? "남자" : "여자", 
          item.isRevisit ? <span className="revisit-badge">재방문</span> : "신규",
          item.status === "REPORT_READY" ? <span className="report-ready-badge">리포트 준비완료</span> : "등록 완료",
          item.prescriptionStatus === "NORMAL" ? "정상" : <span className="cancel-badge">처방취소</span>
        ]);
        const total = response.data.data.total;
        setWorklistData(prev => ({
          ...prev,
          body: formattedData,
          total
        }));
   
        return response.data;

      } catch (error) {
        console.log(error);
        throw new Error(error.response?.data?.msg?.ko || '데이터를 가져오는데 실패했습니다');
      }
    },
    onSuccess: (data) => {
      if (!Array.isArray(data.data)) {
        console.error('데이터 형식이 올바르지 않습니다');
        return;
      }
    },
    onError: (error) => {
      console.error('작업 목록 조회 중 오류 발생:', error);
      setWorklistData(prev => ({
        ...prev,
        body: []
      }));
    },
    refetchInterval: 60000 // 1분(60000ms)마다 자동으로 refetch
  });

  const branchOptions = [
    { value: "0", label: "전체" },
    { value: "54", label: "서울서부" },
    { value: "55", label: "서울동부" },
    { value: "56", label: "강남" },  // 강남 branch added
    { value: "57", label: "부산동부" },
    { value: "58", label: "부산서부" },
    { value: "59", label: "대구" },
    { value: "60", label: "인천" },
    { value: "61", label: "울산" },
    { value: "62", label: "경기" },
    { value: "63", label: "강원" },
    { value: "67", label: "전남" },
    { value: "68", label: "경북" },
    { value: "69", label: "경남" }
  ];

  const prescriptionStatusOptions = [
    { value: "ALL", label: "전체" },
    { value: "NORMAL", label: "정상" },
    { value: "CANCELED", label: "처방취소" }
  ];

  if (isLoading) return <Loading />;

  if (isError) {
    if (error.response) {
      return <div>{error.response.data.msg.ko}</div>
    }
    return <div>{error.message}</div>
  }

  return (
    <>
    <TableFilterBox
    handleClickReset={() => {
      setFilter(defaultFilter);
    }}
    handleClickSubmit={() => {
      refetch();
    }}
  >
     <Row className="justify-content-between">
          <Col sm={6} xxl={5}>
            <Form.Group as={Row}>
              <Form.Label column sm={2} xxl={3}>
                지점
              </Form.Label>
              <Col sm={10} xxl={9}>
                <SelectBox 
                  data={branchOptions}
                  value={filter.branchId}
                  onChange={(selected) => {
                    setFilter(prev => ({
                      ...prev,
                      branchId: selected.value
                    }));
                  }}
                />
              </Col>
            </Form.Group>
          </Col>

          <Col sm={6} xxl={5}>
            <Form.Group as={Row}>
              <Form.Label column sm={2} xxl={3}>
                처방상태
              </Form.Label>
              <Col sm={10} xxl={9}>
                <SelectBox 
                  data={prescriptionStatusOptions}
                  value={filter.prescriptionStatus}
                  onChange={(selected) => {
                    setFilter(prev => ({
                      ...prev,  
                      prescriptionStatus: selected.value
                    }));
                  }}
                />
              </Col>
            </Form.Group>
          </Col>
        </Row>

        <Row className="mt-4 justify-content-between">
          <Col sm={6} xxl={5}>
            <Form.Group as={Row}>
              <Form.Label column sm={2} xxl={3}>
                측정일
              </Form.Label>
              <Col sm={10} xxl={9}>
                <DatepickerRange 
                  startDate={filter.startDate}
                  endDate={filter.endDate}
                  onChange={(dates) => {
                    setFilter(prev => ({
                      ...prev,
                      startDate: dates[0],
                      endDate: dates[1]
                    }));
                  }}
                />
              </Col>
            </Form.Group>
          </Col>
        </Row>
      </TableFilterBox>
    <TableWrap className="mt-4">
        {worklistData?.total > 0 && <TableHeader total={worklistData.total} >
          <div style={{width: "200px"}}>
            <SelectBox 
              onChange={(value) => {
                setFilter({
                  ...filter,
                  page: 1,
                  perPage: value.value
                })
              }} 
              className="w-auto"
              defaultValue={filter.perPage}
              data={perPageOptions}
            />
          </div>
        </TableHeader>}
       
      <DataTable 
        isError={isError}
        isLoading={isLoading}
        data={worklistData}
        useNo={true}
        sort={[0,1]}
        activeSortIdx={1}
        page={filter.page}
        perPage={filter.perPage}
        handleClickSort={(idx, type) => {
          setFilter({
            ...filter,
            orderField: worklistData.orderField[idx],
            order: type,
          });
        }}
      />
          {
                    data && 
                    <TableFooter>
                        <TablePagination 
                        total={data.data.total} 
                        perPage={filter.perPage} 
                        currentPage={filter.page}
                        onChange={(value) => {
                            setFilter({
                                ...filter,
                                page: value
                            })
                        }}/>
                    </TableFooter>
                }
    </TableWrap>
    </>
  );
}

export default Worklist;